'use client';

import React, { useCallback, useEffect, useState } from 'react';
import {
	BigNum,
	BN,
	MarketType,
	ZERO,
	SpotMarketConfig,
	SpotMarketAccount,
} from '@velocity-exchange/sdk';
import { usePathname } from 'next/navigation';
import BuySellToggle from 'src/components/Buttons/BuySellToggle';
import SpotLeverageSliderInput from 'src/components/TradeForm/LeverageSliderInput/SpotLeverageSliderInput';
import useAccountData from 'src/hooks/useAccountData';
import useAccountTargetSpotBalance from 'src/hooks/useAccountTargetSpotBalance';
import { useCurrentAccountMarginInfo } from 'src/hooks/query/accountTradingData/useMarginInfo';
import { useOpenSpotOrder } from 'src/hooks/driftActions/spot/useOpenSpotOrder';
import NumLib from 'src/utils/NumLib';
import useCurrentSettings from '../../hooks/useCurrentSettings';
import useWalletIsConnected from '../../hooks/useWalletIsConnected';
import useDriftStore from '../../stores/DriftStore/useDriftStore';
import SpotTradeDetails from './TradeDetails/SpotTradeDetails';
import InlineAlert from 'src/components/primitives/InlineAlert/InlineAlert';
import useTradeFormInputStateEngine from './hooks/useTradeFormInputStateEngine';
import { useSpotTradeFormStateEngine } from './hooks/useSpotTradeFormStateEngine';
import useOraclePrice from 'src/hooks/useOraclePrice';
import {
	MarketId,
	UIMarket,
	roundToStepSizeIfLargeEnough,
} from '@velocity-exchange/common';
import PriceDivergenceWarningMessage from '../PriceDivergenceWarningMessage';
import useInfoForCurrentlySelectedMarket from '../../hooks/useInfoForCurrentlySelectedMarket';
import { cj } from 'src/utils/classNames';
import { safeCheckDevSwitchIsOn } from 'src/hooks/useDevSwitchIsOn';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import {
	useSetTradeFormPriceBox,
	useSetTradeFormSecondaryPriceBox,
} from 'src/components/TradeForm/hooks/useSetTradeFormInputState';
import usePostHogCapture from 'src/hooks/posthog/usePostHogCapture';
import Checkbox from 'src/components/primitives/Checkbox/Checkbox';
import SizeInput from 'src/components/TradeForm/SizeInput/SizeInput';
import PriceInput from 'src/components/TradeForm/PriceInput/PriceInput';
import Button from '../primitives/Button/Button';
import { PriceImpactInfo } from 'src/utils/priceImpactUtils';
import { SPOT_ORDER_TYPE_OPTIONS } from 'src/components/TradeForm/consts';
import OrderTypeBottomDrawerSelect from 'src/components/TradeForm/BottomDrawerSelectOrderType';
import ApyBanners from 'src/components/ApyBanners/ApyBanners';
import useModal from 'src/components/Modals/hooks/useModal';
import OrderTypeTabs from 'src/components/TradeForm/OrderTypeTabs';
import Slider from 'src/components/primitives/Slider/Slider';
import Link from 'src/components/primitives/Link/Link';
import { SizeUnit } from 'src/components/TradeForm/SizeInput/types';
import useTradeFormHandleSizeInputChange from 'src/components/TradeForm/hooks/useTradeFormHandleSizeInputChange';
import MarginCheckbox from 'src/components/TradeForm/MarginCheckbox';
import { useAppInitialLoadContext } from 'src/providers/app/AppInitialLoadProvider/AppInitialLoadProvider';

export const SpotTradeForm = () => {
	const tradeFormDerivedState = useSpotTradeFormStateEngine();

	// This hook keeps the input values in the drift store in sync
	useTradeFormInputStateEngine();

	const set = useDriftStore((s) => s.set);
	const connected = useWalletIsConnected();
	const selectedUiMarket = useDriftStore((s) => s.selectedMarket.current);
	const currentlySelectedMarketInfo = useInfoForCurrentlySelectedMarket();
	const selectedMarket = selectedUiMarket?.market;
	const currentAccount = useAccountData();
	const { captureEvent } = usePostHogCapture();
	const { show } = useModal();

	const tradeFormStoreState = useDriftStore((s) => s.tradeForm);
	const oraclePrice = useOraclePrice(selectedUiMarket?.marketId as MarketId);
	const [hasAgreedToPriceDivergence, setHasAgreedToPriceDivergence] =
		useState(false);
	const { isInitialLoading } = useAppInitialLoadContext();

	const openSpotOrder = useOpenSpotOrder();

	const pathname = usePathname();

	const isMobileScreenSize = useIsMobileScreenSize();

	const setCurrentOrderType = (orderType: string) =>
		set((s) => {
			//@ts-ignore
			s.tradeForm.orderType = orderType;
		});

	const [savedSettings] = useCurrentSettings();
	const { showTradeConfirmation } = savedSettings;

	const [isSendingTransaction, setIsSendingTransaction] = useState(false);

	const currentPosition = useAccountTargetSpotBalance(
		selectedMarket as SpotMarketConfig,
		currentAccount?.userKey ?? ''
	);

	const currentPositionSize =
		currentPosition?.netQuoteValue?.toNum() ?? undefined;

	const currentPositionBaseSize =
		currentPosition?.netBaseBalance?.abs() ?? undefined;

	const currentPositionDirection =
		(currentPositionBaseSize?.gtZero() ?? true) ? 'long' : 'short';

	const basePrecisionToUse = (selectedMarket as SpotMarketConfig).precisionExp;

	const marginInfo = useCurrentAccountMarginInfo();
	const totalCollateral = marginInfo?.totalCollateral ?? new BN(0);
	const freeCollateral = marginInfo?.freeCollateral ?? new BN(0);

	const totalCurrentPositionSize = NumLib.formatBn.fromQuote(
		totalCollateral
			.sub(freeCollateral)
			.mul(new BN(tradeFormDerivedState.maxLeverageAvailable))
	);

	const currentLeverage = marginInfo?.leverage ?? 0;

	const isGeoblocked = useDriftStore((s) => s.isGeoblocked);

	const currentMarketSymbol = selectedUiMarket?.uiSymbols.marketSymbol;
	const formattedMarketSymbol = selectedUiMarket?.uiSymbols.baseAssetSymbol;

	const toggleReduceOnly = (updatedReduceOnly: boolean) => {
		if (!reduceOnly) {
			if (rawBaseValue.gt(currentPositionBaseSize)) {
				handleSizeInputChange(currentPositionBaseSize.toTradePrecision(), {
					sizeUnit: 'base',
				});
			}
		}

		set((s) => {
			s.tradeForm.reduceOnly = updatedReduceOnly;
		});
	};

	const togglePostOnly = () => {
		if (!postOnly) {
			set((s) => {
				s.tradeForm.immediateOrCancel = false;
			});
		}

		set((s) => {
			s.tradeForm.postOnly = !s.tradeForm.postOnly;
		});
	};

	useEffect(() => {
		// scaledOrders is not support on spot yet, hence switch to default limit
		if (tradeFormStoreState.orderType === 'scaledOrders') {
			setCurrentOrderType('limit');
		}
	}, [tradeFormStoreState.orderType]);

	const {
		side,
		quoteSizeStringValue,
		baseSizeStringValue,
		reduceOnly,
		postOnly,
	} = useDriftStore((s) => ({
		side: s.tradeForm.side,
		quoteSizeStringValue: s.tradeForm.quoteSizeStringValue,
		baseSizeStringValue: s.tradeForm.baseSizeStringValue,
		slippageTolerance: s.tradeForm.slippageTolerance,
		reduceOnly: s.tradeForm.reduceOnly,
		postOnly: s.tradeForm.postOnly,
	}));
	const stepSize = tradeFormDerivedState.stepSize;

	const rawQuoteValue = Number(quoteSizeStringValue);
	const rawBaseValue = BigNum.fromPrint(
		baseSizeStringValue,
		basePrecisionToUse
	);

	const { priceBoxStringValue, secondaryPriceBoxStringValue } = useDriftStore(
		(s) => s.tradeForm
	);

	const setPriceBoxStringValue = useSetTradeFormPriceBox();
	const setSecondaryPriceBoxStringValue = useSetTradeFormSecondaryPriceBox();

	async function onSubmit() {
		try {
			// hardcode 2 seconds diable after submit
			setIsSendingTransaction(true);
			setTimeout(() => {
				setIsSendingTransaction(false);
			}, 3000);

			if (!connected && pathname) {
				set((s) => {
					s.wallet.lastRouteBeforeConnect = pathname;
				});
				show({ type: 'connect_wallet', props: {} });
				return;
			}

			if (showTradeConfirmation && selectedUiMarket) {
				show({
					type: 'trade_confirmation',
					props: {
						tradeFormDetails: {
							...tradeFormStoreState,
							market: selectedUiMarket.marketId,
						},
					},
				});

				return;
			} else {
				const nextOrderId =
					currentAccount?.client?.getUserAccountOrThrow().nextOrderId;
				const isNewAccount = nextOrderId === 1;

				const toastId = `spot-order-${tradeFormStoreState.orderType}-${Date.now()}`;
				await openSpotOrder(toastId, {
					...tradeFormStoreState,
					priceImpact: tradeFormStoreState.priceImpact as PriceImpactInfo,
					targetMarketIndex: selectedMarket?.marketIndex ?? 0,
					currentPositionBaseSize: currentPositionBaseSize?.val ?? ZERO,
					currentPositionDirection,
					oraclePrice: oraclePrice?.val ?? ZERO,
					spotMarketAccount: currentlySelectedMarketInfo.info
						?.account as SpotMarketAccount,
				});

				captureEvent('trade_placed', {
					market_type: 'spot',
					trade_base: +tradeFormStoreState.baseSizeStringValue,
					trade_notional: +tradeFormStoreState.quoteSizeStringValue,
					market_symbol: currentlySelectedMarketInfo.info?.config.symbol,
					is_new_account: isNewAccount,
					next_order_id: nextOrderId,
					order_type: tradeFormStoreState.orderType,
					geolocation_history: useDriftStore.getState().geolocationHistory,
					is_devmode: safeCheckDevSwitchIsOn(),
				});
			}
		} finally {
			//
		}
	}

	const handleInputSourceChange = useCallback(
		(side: SizeUnit) => {
			set((s) => {
				s.tradeForm.leadSide = side;
			});
		},
		[set]
	);

	const setSide = (side: 'buy' | 'sell') => {
		set((s) => {
			s.tradeForm.side = side;
		});
	};

	const handleSizeInputChange = useTradeFormHandleSizeInputChange({
		stepSize: tradeFormDerivedState.stepSize,
	});

	const onDirectionChange = useCallback((side: 'buy' | 'sell') => {
		setSide(side);
		set((s) => {
			s.tradeForm.bracketOrders = {
				takeProfit: undefined,
				stopLoss: undefined,
			};
		});
	}, []);

	const onOrderTypeChange = useCallback((newValue: string) => {
		setCurrentOrderType(newValue);
	}, []);

	const onPriceBoxChange = useCallback((value: string) => {
		setPriceBoxStringValue(value);
	}, []);

	const onSecondaryPriceBoxChange = useCallback((value: string) => {
		setSecondaryPriceBoxStringValue(value);
	}, []);

	const handleLeverageSliderChange = useCallback(
		(v: number) => {
			const newValue = Math.floor(v * 10000) / 10000;
			const valueToSet = newValue
				? newValue.toFixed(2)
				: // If we've slid the value down to `0`, clear the input.
					'';

			handleSizeInputChange(valueToSet, {
				// The leverage slider only operates on the quote size.
				sizeUnit: 'quote',
			});
		},
		[handleSizeInputChange]
	);

	const handlePositionSliderChange = useCallback(
		(v: number) => {
			const newValue = currentPositionBaseSize.scale(v, 100);

			// If we've slid the value down to `0`, clear the input.
			const valueToSet = newValue.eqZero() ? '' : newValue.printShort(true);

			handleSizeInputChange(valueToSet, {
				// The position slider only operates on the base size.
				sizeUnit: 'base',
			});
		},
		[currentPositionBaseSize, handleSizeInputChange]
	);

	const saveLimitPrice = useCallback(
		(value: string) => {
			if (tradeFormStoreState.orderType !== 'limit') return;
			set((s) => {
				s.tradeForm.savedLimitPrice = isNaN(Number(value))
					? null
					: {
							market: UIMarket.createSpotMarket(
								selectedMarket?.marketIndex ?? 0
							).marketId as MarketId,
							price: Number(value),
						};
			});
		},
		[tradeFormStoreState.orderType, selectedMarket?.marketIndex]
	);

	const size = isMobileScreenSize ? 'md' : 'lg';

	const placeOrderButton = connected ? (
		<Button
			className="w-full"
			size={size}
			variant={side === 'buy' ? 'positive' : 'negative'}
			disabled={
				tradeFormDerivedState.tradeButtonIsDisabled ||
				(tradeFormDerivedState.showPriceEstimateOracleDivergenceWarning &&
					!showTradeConfirmation &&
					!hasAgreedToPriceDivergence) ||
				(isGeoblocked && !reduceOnly) ||
				isSendingTransaction
			}
			onClick={onSubmit}
		>
			{tradeFormDerivedState.marketInSettlement
				? `${currentMarketSymbol} in settlement`
				: `${side === 'buy' ? 'Buy' : 'Sell'} ${currentMarketSymbol}`}
		</Button>
	) : (
		<Button
			className="w-full"
			size={size}
			onClick={onSubmit}
			disabled={isGeoblocked || isInitialLoading}
		>
			{isGeoblocked ? 'Unavailable' : 'Connect Wallet'}
		</Button>
	);

	return (
		<div className="flex h-full flex-col">
			<div
				className={cj(
					'flex flex-col gap-y-md',
					'relative z-10' // Setting this to z-10 so that the BlurOverlay is relatively higher than the z-index of the form content
				)}
			>
				<BuySellToggle onChange={onDirectionChange} />

				<div
					className={cj(
						'flex flex-col',
						isMobileScreenSize ? 'gap-y-md' : 'gap-y-lg'
					)}
				>
					{!isMobileScreenSize && (
						<OrderTypeTabs
							currentOrderType={tradeFormStoreState.orderType}
							orderTypeOptions={SPOT_ORDER_TYPE_OPTIONS}
							onOrderTypeChange={onOrderTypeChange}
						/>
					)}
					<div className="flex flex-col gap-y-sm">
						{isMobileScreenSize && (
							<OrderTypeBottomDrawerSelect marketType={MarketType.SPOT} />
						)}

						{tradeFormDerivedState.priceBoxEnabled && (
							<PriceInput
								value={priceBoxStringValue}
								handleChange={onPriceBoxChange}
								handleBlur={saveLimitPrice}
								marketIndex={selectedMarket?.marketIndex ?? 0}
								marketType={MarketType.SPOT}
								label={tradeFormDerivedState.priceBoxHeading}
								showMidButton={tradeFormStoreState.orderType === 'limit'}
								allowNegative={tradeFormStoreState.orderType === 'oracleLimit'}
								baseAssetSymbol={formattedMarketSymbol ?? ''}
							/>
						)}

						{tradeFormDerivedState.showSecondaryPriceBox && (
							<PriceInput
								value={secondaryPriceBoxStringValue}
								handleChange={onSecondaryPriceBoxChange}
								handleBlur={saveLimitPrice}
								marketIndex={selectedMarket?.marketIndex ?? 0}
								marketType={MarketType.SPOT}
								label="Price"
								showMidButton={['stopLimit', 'takeProfitLimit'].includes(
									tradeFormStoreState.orderType
								)}
								baseAssetSymbol={formattedMarketSymbol ?? ''}
							/>
						)}
						<SizeInput
							baseSizeStringValue={roundToStepSizeIfLargeEnough(
								baseSizeStringValue,
								stepSize
							)}
							formattedMarketSymbol={formattedMarketSymbol ?? ''}
							quoteSizeStringValue={quoteSizeStringValue}
							maxQuoteAvailable={tradeFormDerivedState.maxQuoteAvailable}
							stepSize={stepSize}
							onBaseSizeChange={handleSizeInputChange}
							onQuoteSizeChange={handleSizeInputChange}
							onInputSourceChange={handleInputSourceChange}
							sizeErrorMessage={tradeFormDerivedState.sizeErrorMessage?.message}
						/>

						{tradeFormDerivedState.isAccountMarginEnabled &&
							tradeFormDerivedState.showMarginCheckbox &&
							tradeFormDerivedState.isMarginCheckboxChecked && (
								<SpotLeverageSliderInput
									currentLeverage={currentLeverage}
									targetTradeQuoteSize={rawQuoteValue}
									totalCurrentPositionSize={totalCurrentPositionSize}
									targetMarketCurrentPositionSize={currentPositionSize}
									sameSide={
										!tradeFormDerivedState.currentPosition?.baseSize ||
										tradeFormDerivedState.currentPosition?.baseSize.eq(ZERO) ||
										(tradeFormDerivedState.currentPosition?.baseSize?.isNeg()
											? 'sell'
											: 'buy') === side
									}
									maxQuoteSize={tradeFormDerivedState.maxQuoteAvailable}
									maxLeverage={tradeFormDerivedState.maxLeverageAvailable}
									onCurrentSizeChange={handleLeverageSliderChange}
								/>
							)}

						{tradeFormDerivedState.showReducePositionSlider && (
							<Slider
								value={parseFloat(
									currentPositionBaseSize.eqZero()
										? '0'
										: rawBaseValue.toPercentage(currentPositionBaseSize, 4)
								)}
								onValueChange={handlePositionSliderChange}
								onValueCommit={handlePositionSliderChange}
							/>
						)}
					</div>

					<div className="grid grid-cols-2 justify-between gap-y-3">
						{tradeFormDerivedState.showReduceOnly && (
							<Checkbox.Default
								id="spot-trade-form-reduce-only"
								label="Reduce Only"
								checked={reduceOnly && !postOnly}
								onCheckedChange={() => toggleReduceOnly(!reduceOnly)}
								tooltip={{ definitionId: 'tradingReduceOnly' }}
								disabled={tradeFormDerivedState.isReduceOnlyDisabled}
								className="w-fit"
							/>
						)}
						{tradeFormDerivedState.showPostOnly && (
							<Checkbox.Default
								id="spot-trade-form-post-only"
								label="Post Only"
								checked={postOnly && !reduceOnly}
								onCheckedChange={togglePostOnly}
								tooltip={{ definitionId: 'tradingPostOnly' }}
								disabled={reduceOnly}
								className="w-fit justify-self-end"
							/>
						)}
						{tradeFormDerivedState.showMarginCheckbox && (
							<MarginCheckbox
								className="nth-[2]:justify-self-end"
								isAccountMarginEnabled={
									tradeFormDerivedState.isAccountMarginEnabled
								}
								checked={tradeFormDerivedState.isMarginCheckboxChecked}
								onCheckedChange={
									tradeFormDerivedState.toggleIsMarginCheckboxChecked
								}
							/>
						)}
					</div>
				</div>
			</div>

			<div className={isMobileScreenSize ? 'py-md' : 'py-2xl'}>
				{placeOrderButton}
			</div>

			<div
				className={cj(
					'flex flex-col',
					isMobileScreenSize ? 'gap-y-md' : 'gap-y-lg'
				)}
			>
				<SpotTradeDetails
					orderType={tradeFormStoreState.orderType}
					showSlippageTolerance={tradeFormDerivedState.showSlippageTolerance}
				/>

				{tradeFormDerivedState.showPriceEstimateOracleDivergenceWarning &&
					!showTradeConfirmation && (
						<PriceDivergenceWarningMessage
							hasAgreedToPriceDivergence={hasAgreedToPriceDivergence}
							setHasAgreedToPriceDivergence={setHasAgreedToPriceDivergence}
						/>
					)}

				{tradeFormDerivedState.shouldShowMessage && (
					<InlineAlert
						variant={tradeFormDerivedState.messageType}
						title={tradeFormDerivedState.messageTitle}
					>
						{tradeFormDerivedState.message && (
							<span>{tradeFormDerivedState.message}</span>
						)}
						{tradeFormDerivedState.messageLink && (
							<Link href={tradeFormDerivedState.messageLink} openInNewTab>
								{tradeFormDerivedState.messageLinkDescription}
							</Link>
						)}
					</InlineAlert>
				)}

				<ApyBanners />
			</div>
		</div>
	);
};
